import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './config';
import { deleteProductImage } from './storage';
import { Product } from '../types';

const PRODUCTS_COLLECTION = 'products';

const checkAuth = () => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('Missing or insufficient permissions.');
  }
};

export const addProduct = async (productData: Omit<Product, 'id' | 'createdAt'>) => {
  try {
    checkAuth();
    const docRef = await addDoc(collection(db, PRODUCTS_COLLECTION), {
      ...productData,
      createdAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding product:', error);
    throw error;
  }
};

export const updateProduct = async (id: string, productData: Partial<Product>) => {
  try {
    checkAuth();
    const productRef = doc(db, PRODUCTS_COLLECTION, id);
    await updateDoc(productRef, {
      ...productData,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

export const deleteProduct = async (id: string) => {
  try {
    checkAuth();
    const productRef = doc(db, PRODUCTS_COLLECTION, id);
    const productSnapshot = await getDocs(query(collection(db, PRODUCTS_COLLECTION)));
    const product = productSnapshot.docs.find(doc => doc.id === id);

    if (product) {
      const productData = product.data() as Product;
      // Delete all product images
      const deletePromises = productData.images.map(image => deleteProductImage(image.url));
      await Promise.all(deletePromises);
    }

    await deleteDoc(productRef);
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

export const getProducts = async (): Promise<Product[]> => {
  try {
    const q = query(
      collection(db, PRODUCTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
    })) as Product[];
  } catch (error) {
    console.error('Error getting products:', error);
    throw error;
  }
};